import { Coins, Phone, CheckCircle2, ShieldCheck, CreditCard, Smartphone, ShieldAlert } from 'lucide-react';
import ContactForm from '../../components/ContactForm';
import SEO from '../../components/SEO';

export default function PrivatfinansieringDe() {
  const anydayFordele = [
    {
      title: 'Flexible Ratenzahlung',
      desc: 'Teilen Sie den Betrag in überschaubare monatliche Raten auf, passend zu Ihrem Budget.',
      icon: <CreditCard className="h-6 w-6 text-emerald-600" aria-hidden="true" />
    },
    {
      title: 'Bis zu 24 Monate',
      desc: 'Wählen Sie eine Laufzeit von bis zu 24 Monaten für maximale finanzielle Freiheit.',
      icon: <Smartphone className="h-6 w-6 text-emerald-600" aria-hidden="true" />
    },
    {
      title: 'Schnelle Online-Prüfung',
      desc: 'Die Antragstellung erfolgt schnell, unkompliziert und sicher online mit sofortiger Rückmeldung.',
      icon: <ShieldCheck className="h-6 w-6 text-emerald-600" aria-hidden="true" />
    }
  ];

  const trinGuide = [
    { step: '01', title: 'Maschine auswählen', desc: 'Wählen Sie Ihren Danshaker und gewünschtes Zubehör.' },
    { step: '02', title: 'Online beantragen', desc: 'Füllen Sie den Antrag digital in wenigen Minuten aus.' },
    { step: '03', title: 'Sofortige Zusage', desc: 'Sie erhalten umgehend Ihre Bestätigung.' },
    { step: '04', title: 'Lieferung zu Ihnen', desc: 'Nach Vertragsunterzeichnung starten wir die Fertigung und liefern zu Ihrem Stall.' }
  ];

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-950 antialiased">
      <SEO
        title="Privatfinanzierung für den Danshaker - Flexible Ratenzahlung"
        description="Finanzieren Sie Ihre Danshaker Entmistungs- und Sortiermaschine privat mit flexiblen Ratenmodellen ab nur ca. 1.563 DKK/Monat inkl. MwSt."
        path="/de/privatfinanzierung"
        lang="de"
      />

      {/* FinTech Header / Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-emerald-50 to-slate-50 py-16 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 md:px-8 relative z-10">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
            
            {/* Left: Intro */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 border border-emerald-300 px-4 py-1.5 text-xs font-black text-emerald-800 uppercase tracking-wider">
                <Coins className="h-3.5 w-3.5" aria-hidden="true" />
                <span>Finanzierung für Privatkunden</span>
              </div>
              <h1 className="text-4xl font-black tracking-tight sm:text-6xl text-slate-950">
                Privatfinanzierung
              </h1>
              <p className="text-lg sm:text-xl text-slate-900 font-semibold leading-relaxed">
                Erleichtern Sie sich Ihren Stallalltag ab sofort! Wir arbeiten mit etablierten Finanzierungspartnern wie <strong className="text-emerald-700">Anyday</strong> zusammen, damit Sie Ihren Danshaker bequem in bequemen Monatsraten finanzieren können.
              </p>
              
              {/* Highlighted Price Box */}
              <div className="inline-block rounded-2xl border-2 border-emerald-300 bg-white p-6 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 left-0 h-2 w-full bg-emerald-500" />
                <span className="block text-xs text-slate-900 font-black uppercase tracking-wider mb-1">Monatliche Rate ab</span>
                <div className="text-4xl sm:text-5xl font-black text-emerald-600">
                  ca. 210,- €
                </div>
                <span className="text-xs text-slate-800 font-bold mt-1 block">inkl. dänischer MwSt. (bei 24 Monaten Laufzeit)</span>
              </div>
            </div>

            {/* Right: Quick Perks */}
            <div className="lg:col-span-5">
              <div className="rounded-3xl border-2 border-emerald-300 bg-white p-6 md:p-8 shadow-2xl space-y-6">
                <h2 className="text-2xl font-black text-slate-950">Vorteile der Ratenzahlung</h2>
                
                <div className="space-y-4">
                  {anydayFordele.map((f, i) => (
                    <div key={i} className="flex items-start gap-4 p-3 rounded-2xl bg-emerald-50/50 border border-emerald-100">
                      <div className="shrink-0 mt-1">{f.icon}</div>
                      <div>
                        <h3 className="font-black text-slate-950 text-base">{f.title}</h3>
                        <p className="text-xs text-slate-800 font-bold leading-relaxed mt-0.5">{f.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs text-slate-800 font-bold">
                  <div className="flex items-center gap-1.5">
                    <ShieldCheck className="h-4 w-4 text-emerald-600" />
                    <span>Sichere digitale Abwicklung</span>
                  </div>
                  <span>Keine versteckten Gebühren</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-8">
        
        {/* Step Guide */}
        <div className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl font-black text-slate-950">In 4 Schritten zu Ihrem Danshaker</h2>
            <p className="mt-2 text-slate-900 font-bold">So einfach ist die private Teilzahlung.</p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {trinGuide.map((t) => (
              <div key={t.step} className="rounded-2xl border-2 border-slate-300 bg-white p-6 shadow-sm relative">
                <span className="text-3xl font-black text-emerald-600">{t.step}</span>
                <h4 className="text-lg font-black text-slate-950 mt-2 mb-1">{t.title}</h4>
                <p className="text-xs text-slate-900 font-bold leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Form and Contact */}
        <div className="grid gap-12 lg:grid-cols-12 items-start">
          
          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-3xl border-2 border-slate-300 bg-white p-6 md:p-8 shadow-xl space-y-4">
              <h3 className="text-2xl font-black text-slate-950">Fragen zur Privatfinanzierung?</h3>
              <p className="text-sm text-slate-900 font-bold leading-relaxed">
                Rufen Sie uns gerne an, wenn Sie wissen möchten, welches Ratenmodell am besten zu Ihnen passt oder wie die Abwicklung im Detail funktioniert.
              </p>
              <div className="pt-2">
                <a
                  href="tel:25303589"
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-6 py-3 font-bold text-white transition hover:bg-slate-800"
                >
                  <Phone className="h-4 w-4 text-emerald-400" />
                  +45 2530 3589
                </a>
              </div>
            </div>

            <div className="rounded-3xl bg-amber-50 border-2 border-amber-300 p-6 text-amber-950 text-xs font-bold leading-relaxed">
              <div className="flex items-center gap-2 mb-2 font-black text-amber-900">
                <ShieldAlert className="h-5 w-5 shrink-0" />
                <span>Wichtige Information zur Ratenzahlung:</span>
              </div>
              Die Bonitätsprüfung und Genehmigung erfolgt durch den jeweiligen Zahlungsanbieter. Berechnungsbeispiele basieren auf dem Standardpreis von 4.225 € inkl. dänischer MwSt.
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-3xl border-2 border-emerald-500 bg-white p-6 md:p-10 shadow-2xl">
              <div className="mb-8">
                <span className="text-xs font-black uppercase tracking-wider text-emerald-700">Unverbindlich anfragen</span>
                <h3 className="text-2xl md:text-3xl font-black text-slate-950 mt-1">Privatfinanzierung anfragen</h3>
                <p className="mt-2 text-sm text-slate-900 font-bold">
                  Senden Sie uns eine unverbindliche Anfrage – wir unterstützen Sie beim schnellen Ablauf.
                </p>
              </div>

              <ContactForm defaultService="Privatfinanzierung" buttonText="Finanzierung anfragen" lang="de" />
            </div>
          </div>

        </div>

      </section>
    </div>
  );
}
